import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authenticatedPost, handleApiResponse } from '../../utils/apiClient';
import NavBar from './NavBar';
import Footer from '../../Footer';
import './Pricing.css';

// Determine API URL (same pattern as QuestionBox)
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return 'https://us-central1-clarifyfact-afa06.cloudfunctions.net/api';
  }
  
  if (process.env.NODE_ENV === 'production') {
    return 'https://us-central1-clarifyfact-afa06.cloudfunctions.net/api';
  }
  
  return 'http://localhost:5000';
};

const API_BASE_URL = getApiBaseUrl();

const getApiUrl = (endpoint) => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  if (API_BASE_URL.includes('/api')) {
    return `${API_BASE_URL}${cleanEndpoint}`;
  }
  return `${API_BASE_URL}/api${cleanEndpoint}`;
};

function Pricing() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const { currentUser, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Check for Stripe redirect success/cancel
  useEffect(() => {
    const successParam = searchParams.get('success');
    const canceledParam = searchParams.get('canceled');
    const sessionId = searchParams.get('session_id');

    if (successParam === 'true' && sessionId) {
      setSuccess('Payment successful! Your words have been added to your account.');
      // Refresh user profile to show updated balance
      if (refreshUserProfile) {
        refreshUserProfile();
      }
      // Clear URL params
      navigate('/pricing', { replace: true });
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } else if (canceledParam === 'true') {
      setError('Payment was canceled. You can try again anytime.');
      // Clear URL params
      navigate('/pricing', { replace: true });
      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  }, [searchParams, navigate, refreshUserProfile]);

  // Word amounts for each plan
  const wordAmounts = {
    'Starter': 10000, // 10,000 words per month
    'Pro': 50000, // 50,000 words per month
  };

  const plans = {
    monthly: [
      {
        name: 'Starter',
        price: '$3.98',
        period: '/month',
        features: [
          'Personalized Results',
          'Up to 10,000 words/month',
          'Basic AI humanization',
          'Standard support',
          'Email support'
        ],
        buttonText: 'Subscribe',
        popular: false
      },
      {
        name: 'Pro',
        price: '$9.99',
        period: '/month',
        features: [
          'Personalized Results',
          'Up to 50,000 words/month',
          'Advanced AI humanization',
          'Priority support',
          'Advanced writing optimization',
          'Unlimited file uploads',
          'API access'
        ],
        buttonText: 'Subscribe',
        popular: true
      },
      {
        name: 'Enterprise',
        price: 'Contact',
        period: '',
        features: [
          'Personalized Results',
          'Unlimited words',
          'Premium AI humanization',
          'Dedicated support',
          'Custom AI training',
          'Team collaboration',
          'Advanced analytics',
          'API access'
        ],
        buttonText: 'Contact Us',
        popular: false,
        isContact: true
      }
    ],
    annual: [
      {
        name: 'Starter',
        price: '$32.99',
        period: '/year',
        originalPrice: '$47.76',
        features: [
          'Personalized Results',
          'Up to 10,000 words/month',
          'Basic AI humanization',
          'Standard support',
          'Email support'
        ],
        buttonText: 'Subscribe',
        popular: false
      },
      {
        name: 'Pro',
        price: '$59.94',
        period: '/year',
        originalPrice: '$119.88',
        features: [
          'Personalized Results',
          'Up to 50,000 words/month',
          'Advanced AI humanization',
          'Priority support',
          'Advanced writing optimization',
          'Unlimited file uploads',
          'API access'
        ],
        buttonText: 'Subscribe',
        popular: true
      },
      {
        name: 'Enterprise',
        price: 'Contact',
        period: '',
        features: [
          'Personalized Results',
          'Unlimited words',
          'Premium AI humanization',
          'Dedicated support',
          'Custom AI training',
          'Team collaboration',
          'Advanced analytics',
          'API access'
        ],
        buttonText: 'Contact Us',
        popular: false,
        isContact: true
      }
    ]
  };

  const currentPlans = plans[billingCycle];

  // Handle plan purchase with Stripe
  const handlePurchase = async (planName) => {
    if (!currentUser) {
      alert('Please sign in to purchase a plan.');
      navigate('/');
      return;
    }

    if (!wordAmounts[planName]) {
      // Enterprise plan - contact
      alert('hello.clarifyfact@gmail.com');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      // Create Stripe checkout session
      const checkoutUrl = getApiUrl('/create-checkout-session');
      console.log('[Pricing] Creating checkout session:', { planName, billingCycle, url: checkoutUrl });
      
      const response = await authenticatedPost(checkoutUrl, {
        planName: planName,
        billingCycle: billingCycle
      });

      console.log('[Pricing] Checkout session response status:', response.status);
      
      const data = await handleApiResponse(response);
      console.log('[Pricing] Checkout session data:', data);

      if (data.success && data.url) {
        console.log('[Pricing] Redirecting to Stripe Checkout:', data.url);
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        console.error('[Pricing] Invalid response from checkout endpoint:', data);
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('[Pricing] Error creating checkout session:', error);
      console.error('[Pricing] Error details:', {
        message: error.message,
        stack: error.stack
      });
      setError(`Failed to start payment: ${error.message}. Please try again.`);
      setIsProcessing(false);
    }
  };

  return (
    <div className="pricing-page">
      <NavBar />
      
      <div className="pricing-container">
        <h1 className="pricing-title">Flexible pricing plans for you</h1>
        
        {error && (
          <div className="pricing-message pricing-error">
            <strong>Error:</strong> {error}
          </div>
        )}
        
        {success && (
          <div className="pricing-message pricing-success">
            <strong>Success:</strong> {success}
          </div>
        )}
        
        <div className="billing-toggle">
          <button 
            className={`toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly
          </button>
          <button 
            className={`toggle-btn ${billingCycle === 'annual' ? 'active' : ''}`}
            onClick={() => setBillingCycle('annual')}
          >
            Annual
            <span className="save-badge">Save 50%</span>
          </button>
        </div>

        <div className="pricing-cards">
          {currentPlans.map((plan, index) => (
            <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
              {plan.popular && <div className="popular-badge">Most Popular</div>}
              
              <h3 className="plan-name">{plan.name}</h3>
              
              <div className="plan-price">
                {plan.originalPrice && (
                  <span className="original-price">{plan.originalPrice}</span>
                )}
                <span className="price">{plan.price}</span>
                <span className="period">{plan.period}</span>
              </div>
              
              <ul className="plan-features">
                {plan.features.map((feature, i) => (
                  <li key={i}>
                    <span className="check-icon">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              
              <button 
                className={`plan-button ${plan.popular ? 'popular-btn' : ''}`}
                onClick={() => handlePurchase(plan.name)}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : plan.buttonText}
              </button>
            </div>
          ))}
        </div>

        <div className="need-more">
          <p>Need more? <a href="#contact" onClick={(e) => { e.preventDefault(); alert('hello.clarifyfact@gmail.com'); }}>Contact Us</a></p>
        </div>

        <div className="pricing-disclaimer">
          <p>By clicking the Subscribe button, you agree to our <Link to="/terms-of-service">Terms of Service</Link> and <a href="#privacy">Privacy Policy</a>.</p>
          <p className="academic-notice"><strong>IMPORTANT:</strong> ClarifyFact is not a tool for academic dishonesty or cheating. Users are responsible for ensuring their use complies with their institution's academic integrity policies. <Link to="/ethical-usage" className="read-more-link">Read more</Link></p>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default Pricing;

